import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Tracks recovery attempts within the tab. Cleared on the next successful
// chunk load so a *future* stale-deploy event can also recover, but stops
// infinite loops if recovery itself can't fix the underlying problem.
const ATTEMPT_KEY = 'ember:chunk-recover-attempts';
const MAX_ATTEMPTS = 2; // soft-reload, then nuke-and-reload, then give up.

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    err.message,
  );
}

function readAttempts(): number {
  try {
    return Number(window.sessionStorage.getItem(ATTEMPT_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}

function writeAttempts(n: number): void {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, String(n));
  } catch {
    /* sessionStorage may be unavailable (private mode, storage full) */
  }
}

function clearAttempts(): void {
  try {
    window.sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    /* noop */
  }
}

// Unregisters every service worker and wipes every CacheStorage entry. This
// is the recovery path when a soft reload alone didn't help — i.e. when an old
// service worker is intercepting requests with stale precache. We do it before
// reloading so the next navigation fetches everything fresh from the network.
async function nukeServiceWorkersAndCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* swallow — best-effort */
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* swallow */
  }
}

// Wraps React.lazy so that when a code-split chunk fails to load — usually
// because a new build was deployed while the user held an old asset manifest,
// or because a stale service worker is intercepting requests — the app
// attempts increasingly aggressive recovery:
//   1st failure → soft reload (picks up the new index.html via the network).
//   2nd failure → unregister all SWs, clear all CacheStorage, then reload.
//   3rd failure → propagate the error to the ErrorBoundary.
// A successful chunk load clears the attempt counter so a future stale-deploy
// event can self-heal as well.
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearAttempts();
      return mod;
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      const attempts = readAttempts();
      if (attempts >= MAX_ATTEMPTS) throw err;
      writeAttempts(attempts + 1);
      if (attempts === 0) {
        window.location.reload();
      } else {
        await nukeServiceWorkersAndCaches();
        window.location.reload();
      }
      // Hang the promise so React keeps the Suspense fallback rendered
      // until the reload navigates away — avoids a flash of error UI.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
